export function generateEbayTitle({
  artists,
  releaseTitle,
  color,
  format,
  label,
  releaseYear,
  edition,
  type,
  speed,
  recordSize,
  totalQty,
  formats = [],
  series = [],
  condition = "USED",
}) {
  const stripArticles = (str) =>
    typeof str === "string" ? str.replace(/^(The|A)\s+/i, "").trim() : "";

  const cleanDisplayArtist = (name) => {
    if (typeof name !== "string") return "";
    if (name.trim().toLowerCase() === "the band") return name;
    return name
      .replace(/\b(Jazz Workshop|Trio|Quartet|Band)\b$/i, "")
      .replace(/\s{2,}/g, " ")
      .trim()
      .replace(/^The\s+/i, "");
  };

  const cleanLabel = (label) => {
    if (!label) return "";

    // Handle Mobile Fidelity specifically
    if (label.toLowerCase().includes("mobile fidelity sound lab")) {
      return "Mobile Fidelity";
    }


    return label
      .replace(
        /\b(Music Entertainment|Sound Lab|Music Group|Recording Company|Records?|Recordings?|Entertainment|LLC|Inc\.?|Corp\.?)\b/gi,
        ""
      )
      .replace(/\s+/g, " ")
      .trim();
  };

  const normalizeReleaseTitle = (title = "", removeParentheses = false) => {
    let result = title
      // Convert long soundtrack phrases to just "Soundtrack"
      .replace(
        /\(Original (Motion Picture )?(Sound ?Track|Score)\)/gi,
        "Soundtrack"
      )
      .replace(/\(Original Soundtrack\)/gi, "Soundtrack")
      .replace(/\(Music From The Showtime Original Series\)/gi, "Soundtrack")
      // "30th Anniversary Edition" → "30th Anniversary"
      .replace(/\b(Anniversary)\s+Edition\b/gi, "$1");

    // Remove all parentheses and their contents if flag is set
    if (removeParentheses) {
      // Iteratively remove parentheses content (handles nested parentheses)
      let prevLength;
      do {
        prevLength = result.length;
        result = result.replace(/\([^()]*\)/g, "");
      } while (result.length !== prevLength);
    }
    
    return result
      // Clean up extra spaces after parentheses removal
      .replace(/\s{2,}/g, " ")
      .trim();
  };

  const normalizedTitle = normalizeReleaseTitle(releaseTitle);

  const displayArtistString = Array.isArray(artists)
    ? artists.map(cleanDisplayArtist).join(", ")
    : cleanDisplayArtist(artists);

  const normalizedArtist = displayArtistString.trim().toLowerCase();
  const normalizedTitleLower = normalizedTitle.trim().toLowerCase();

  const omitArtist = normalizedTitleLower.startsWith(normalizedArtist);

  const isBoxSet = type?.toLowerCase() === "box set";

  // Base data
  const base = {
    artists: displayArtistString,
    releaseTitle: normalizedTitle,
    color,
    format,
    edition,
    label: cleanLabel(label),
    releaseYear: releaseYear?.toString(),
  };

  // Raw text for special pressing detection
  const rawText = [
    ...(formats || []).map((f) => f.text || ""),
    ...(formats || []).flatMap((f) => f.descriptions || []),
    ...(series || []).map((s) => s.name || ""),
  ]
    .join(" ")
    .toLowerCase();

  const specialPressingTags = [];
  if (rawText.includes("uhqr")) specialPressingTags.push("UHQR");
  if (rawText.includes("one-step")) specialPressingTags.push("One-Step");
  if (rawText.includes("mofi") || rawText.includes("mobile fidelity"))
    specialPressingTags.push("MoFi");
  if (rawText.includes("supervinyl")) specialPressingTags.push("SuperVinyl");
  if (rawText.includes("quadrophonic"))
    specialPressingTags.push("Quadrophonic");

  // Build title function with optimizations
  const buildTitle = (options = {}) => {
    const {
      stripArticles: shouldStripArticles = false,
      removeSoundtrack = false,
      removeParentheses = false,
      includeColor = true,
      includeEdition = true,
      optimize = false,
    } = options;

    let artistPart = base.artists;
    let titlePart = base.releaseTitle;

    // Apply stripping first
    if (shouldStripArticles) {
      artistPart = stripArticles(artistPart);
      titlePart = removeParentheses
        ? stripArticles(normalizeReleaseTitle(releaseTitle, true))
        : removeSoundtrack
        ? stripArticles(normalizedTitle.replace(/\bSoundtrack\b/i, ""))
        : stripArticles(titlePart);
    }

    // Apply optimizations
    if (optimize) {
      // Remove "The" from artist and title
      if (artistPart && artistPart.startsWith("The ")) {
        artistPart = artistPart.replace(/^The\s+/i, "");
      }
      if (titlePart && titlePart.startsWith("The ")) {
        titlePart = titlePart.replace(/^The\s+/i, "");
      }
      
      // Convert Soundtrack to OST
      if (titlePart && titlePart.toLowerCase().includes("soundtrack")) {
        titlePart = titlePart.replace(/soundtrack/gi, "OST");
      }
    }

    const parts = [];

    // Main title part
    if (!omitArtist && artistPart && titlePart) {
      parts.push(`${artistPart} – ${titlePart}`);
    } else {
      parts.push(titlePart || artistPart);
    }

    // Format and color part
    if (base.format) {
      let cleanedColor = base.color
        ?.replace(/\b(Opaque|Translucent|Clear)\b/gi, "")
        .replace(/\s{2,}/g, " ")
        .trim();

      // Optimize color if requested
      if (optimize && cleanedColor) {
        cleanedColor = cleanedColor.replace(/\s+with\s+/gi, " w/ ");
      }

      const colorPart =
        includeColor && cleanedColor && cleanedColor.toLowerCase() !== "black"
          ? `${cleanedColor} `
          : "";

      const pressingPrefix = specialPressingTags.length
        ? `${specialPressingTags.join(" ")} `
        : "";

      // Add 45 RPM for 12" records — signals audiophile pressing
      // Only when the ENTIRE release is 45 RPM (not a bonus 45 single in a 33 RPM album)
      const is12Inch = !recordSize || recordSize === '12"' || recordSize.includes("12");
      const allFormats45 = formats.length > 0 && formats.every((f) => {
        const sources = [...(f.descriptions || []), ...(f.text ? [f.text] : [])].join(" ");
        return /\b45\b/.test(sources);
      });
      const rpmTag = allFormats45 && is12Inch ? " 45 RPM" : "";

      let formatPart;
      if (isBoxSet && totalQty) {
        formatPart = `${colorPart}${pressingPrefix}${totalQty} LP Vinyl Box Set${rpmTag}`;
      } else if (isBoxSet) {
        formatPart = `${colorPart}${pressingPrefix}Vinyl LP Box Set${rpmTag}`;
      } else {
        formatPart = `${colorPart}${pressingPrefix}Vinyl LP${rpmTag}`;
      }

      parts.push(formatPart.trim());
    }

    // Check if this is a VMP record and has Club/Club Edition
    const isVMPRecord = base.label && base.label.toLowerCase().includes("vinyl me please");
    const hasVMPClubEdition = isVMPRecord && base.edition &&
      (base.edition.toLowerCase().includes("club edition") || base.edition.toLowerCase().includes("club"));

    // Edition part (but not for VMP Club editions - those get moved to after label)
    if (
      includeEdition &&
      base.edition &&
      base.edition.toLowerCase() !== "standard" &&
      !hasVMPClubEdition
    ) {
      const shortenedEdition = base.edition
        .replace(/\bLimited Edition\b/gi, "Ltd Ed.")
        .replace(/\bLimited\b/i, "Ltd");
      parts.push(shortenedEdition);
    }

    // Label and year part
    const yearOrEarly = base.releaseYear || "Early Pressing";

    if (base.label && base.releaseYear) {
      let labelPart = `${base.label} ${base.releaseYear}`;

      // Add VMP after the label+year for VMP Club editions
      if (hasVMPClubEdition) {
        labelPart += " VMP";
      }

      parts.push(labelPart);
    } else if (base.label && !base.releaseYear) {
      let labelPart = `${base.label} ${yearOrEarly}`;

      // Add VMP after the label for VMP Club editions
      if (hasVMPClubEdition) {
        labelPart += " VMP";
      }

      parts.push(labelPart);
    } else if (base.releaseYear) {
      parts.push(base.releaseYear);
    } else {
      parts.push(yearOrEarly);
    }

    return parts.join(", ").trim();
  };

  // Define strategies in order of preference
  const strategies = [
    // Basic strategy
    { stripArticles: false, includeColor: true, includeEdition: true },
    
    // Early optimization
    { stripArticles: false, includeColor: true, includeEdition: true, optimize: true },
    
    // Strip articles
    { stripArticles: true, includeColor: true, includeEdition: true },
    { stripArticles: true, includeColor: true, includeEdition: true, optimize: true },
    
    // Remove color
    { stripArticles: true, includeColor: false, includeEdition: true },
    { stripArticles: true, includeColor: false, includeEdition: true, optimize: true },
    
    // Remove soundtrack
    { stripArticles: true, removeSoundtrack: true, includeColor: false, includeEdition: true },
    { stripArticles: true, removeSoundtrack: true, includeColor: false, includeEdition: true, optimize: true },
    
    // Remove edition
    { stripArticles: true, removeSoundtrack: true, includeColor: false, includeEdition: false },
    { stripArticles: true, removeSoundtrack: true, includeColor: false, includeEdition: false, optimize: true },
    
    // Remove parentheses
    { stripArticles: true, removeParentheses: true, includeColor: false, includeEdition: true },
    { stripArticles: true, removeParentheses: true, includeColor: false, includeEdition: false },
  ];

  // Try strategies
  for (const strategy of strategies) {
    const title = buildTitle(strategy);
    if (title.length <= 80) {
      return title;
    }
  }

  // Hard fallback - truncate smartly
  const fallback = buildTitle({ optimize: true });
  if (fallback.length <= 80) {
    return fallback;
  }

  // Find last space within 80 chars to avoid cutting words
  const lastSpaceIndex = fallback.lastIndexOf(" ", 80);
  
  if (lastSpaceIndex === -1 || lastSpaceIndex < 40) {
    return fallback.substring(0, 77).trim() + "...";
  }
  
  const safe = fallback.substring(0, lastSpaceIndex);
  const cleaned = safe.replace(/[,.\-–]+$/, "").trim();
  
  return cleaned;
}